// src/admin/pages/Members.jsx
import React, { useEffect, useMemo, useState, useRef } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Typography,
    Snackbar,
    Alert,
    Fab,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import EmailIcon from '@mui/icons-material/Email';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
    adminMembers,
    adminCreateMember,
    adminUpdateMember,
    adminDeleteMember,
    adminSendEmail,
} from '../../utils/api';
import CrudTable from '../components/CrudTable';

// PDF helpers
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const TYPE_OPTIONS = [
    { value: 'single', label: 'Single' },
    { value: 'couple', label: 'Couple' },
    { value: 'family', label: 'Family' },
];

function title(v) {
    return v ? v.charAt(0).toUpperCase() + v.slice(1) : '';
}
function fmtDate(d) {
    if (!d) return '—';
    const iso = String(d).slice(0, 10);
    const [y, m, dd] = iso.split('-').map(Number);
    return new Date(y, m - 1, dd).toLocaleDateString();
}
function add1y(d) {
    if (!d) return null;
    const dt = new Date(String(d).slice(0, 10));
    dt.setFullYear(dt.getFullYear() + 1);
    return dt.toISOString().slice(0, 10);
}

// Shared helper to format a member's full address
function formatAddress(row) {
    if (!row) return '';
    const parts = [];

    if (row.street_address) {
        parts.push(row.street_address);
    }

    const cityParts = [];
    if (row.city) cityParts.push(row.city);
    if (row.state) cityParts.push(row.state);

    let cityStateZip = cityParts.join(', ');
    if (row.zip_code) {
        cityStateZip = cityStateZip
            ? `${cityStateZip} ${row.zip_code}`
            : row.zip_code;
    }

    if (cityStateZip) {
        parts.push(cityStateZip);
    }

    return parts.join(', ');
}

function EmailDialog({ open, onClose, name, email, onSent }) {
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (open) {
            setSubject('');
            setMessage('');
        }
    }, [open, name, email]);

    const send = async () => {
        if (!email) return alert('No email on file.');
        setSending(true);
        try {
            const res = await adminSendEmail({ to: email, subject, message });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) throw new Error(data?.error || 'Failed to send message.');
            onSent?.();
            onClose?.();
        } catch (e) {
            alert(e.message || 'Failed to send message.');
        } finally {
            setSending(false);
        }
    };
    return (
        <Dialog
            open={open}
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onClose?.();
            }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Send Message</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 2 }}>
                <TextField label="Name" fullWidth value={name || ''} InputProps={{ readOnly: true }} />
                <TextField
                    label="Email"
                    fullWidth
                    value={email || ''}
                    type="email"
                    InputProps={{ readOnly: true }}
                />
                <TextField
                    label="Subject"
                    fullWidth
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                />
                <TextField
                    label="Message"
                    fullWidth
                    multiline
                    minRows={8}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={send}
                    disabled={sending || !subject || !message}
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/* ---------------- Mobile filter popup ---------------- */
function FilterDialog({ open, initial, onSearch, onClear, onCancel }) {
    const [local, setLocal] = useState(initial);
    useEffect(() => {
        if (open) setLocal(initial);
    }, [open, initial]);
    const set = (k) => (e) => setLocal((f) => ({ ...f, [k]: e.target.value }));

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="sm"
            onClose={(e, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
                onCancel?.();
            }}
            disableEscapeKeyDown
        >
            <DialogTitle>Filter Members</DialogTitle>
            <DialogContent dividers sx={{ display: 'grid', gap: 1.25 }}>
                <TextField label="Name" value={local.q} onChange={set('q')} fullWidth />
                <TextField label="Email" value={local.email} onChange={set('email')} fullWidth />
                <TextField label="City" value={local.city} onChange={set('city')} fullWidth />
                <TextField label="State" value={local.state} onChange={set('state')} fullWidth />
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-end' }}>
                <Button
                    onClick={() => {
                        onClear?.();
                    }}
                >
                    Clear
                </Button>
                <Button variant="contained" onClick={() => onSearch?.(local)}>
                    Search
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function MembersPage() {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({});
    const [snack, setSnack] = useState(false);
    const [emailDlg, setEmailDlg] = useState({ open: false, name: '', email: '' });

    // Simple filters
    const [filters, setFilters] = useState({ q: '', email: '', city: '', state: '' });
    const [filterDlgOpen, setFilterDlgOpen] = useState(false);

    // Top-of-page banner for create/edit/delete
    const [banner, setBanner] = useState(null);
    const bannerTimerRef = useRef(null);

    // Sort state
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const showBanner = (text, severity = 'success', ms = 4000) => {
        setBanner({ text, severity });
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = setTimeout(() => setBanner(null), ms);
    };

    useEffect(() => {
        return () => {
            if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
        };
    }, []);

    // Delete confirmation dialog
    const [deleteDlg, setDeleteDlg] = useState({
        open: false,
        row: null,
        busy: false,
        error: '',
    });

    const load = async () => {
        const data = await adminMembers();
        setRows(
            (data.rows || []).map((r) => ({
                ...r,
                Type: title(r.membership_type),
                // "Paid" is driven directly by confirmed (0/1)
                PayFor: r.confirmed ? 1 : 0,
                Start: fmtDate(r.created_at),
                ExpiryISO: add1y(r.created_at),
            }))
        );
    };
    useEffect(() => {
        load();
    }, []);

    const toggleSort = (key) => {
        setSortConfig((prev) =>
            prev.key === key
                ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
                : { key, direction: 'asc' }
        );
    };

    const dateHeaderButtonSx = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        font: 'inherit',
        color: 'inherit',
    };

    const columns = [
        {
            key: 'first_name',
            header: 'Name',
            headerLabel: 'Name',
            nowrap: true,
            render: (_v, r) => {
                const name = `${r.first_name || ''} ${r.last_name || ''}`.trim();
                return name || '—';
            },
        },
        {
            key: 'address',
            header: 'Address',
            headerLabel: 'Address',
            nowrap: false,
            render: (_v, r) => formatAddress(r) || '—',
        },
        {
            key: 'email',
            header: 'Email',
            headerLabel: 'Email',
            nowrap: true,
            render: (v, r) =>
                v ? (
                    <Button
                        size="small"
                        variant="text"
                        startIcon={<EmailIcon />}
                        onClick={(e) => {
                            e.stopPropagation();
                            setEmailDlg({
                                open: true,
                                name: `${r.first_name || ''} ${r.last_name || ''}`.trim(),
                                email: v,
                            });
                        }}
                    >
                        {v}
                    </Button>
                ) : (
                    '—'
                ),
        },
        { key: 'phone', header: 'Phone', headerLabel: 'Phone', nowrap: true },
        { key: 'Type', header: 'Type', headerLabel: 'Type', nowrap: true },
        {
            key: 'PayFor',
            header: 'Paid',
            headerLabel: 'Paid',
            nowrap: true,
            render: (v) => (v ? <CheckIcon /> : '—'),
        },
        {
            key: 'Start',
            headerLabel: 'Membership Started',
            header: (
                <Box
                    component="button"
                    type="button"
                    sx={dateHeaderButtonSx}
                    onClick={() => toggleSort('Start')}
                >
                    Membership Started
                    {sortConfig.key === 'Start' &&
                        (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon fontSize="inherit" />
                        ) : (
                            <ArrowDownwardIcon fontSize="inherit" />
                        ))}
                </Box>
            ),
            nowrap: true,
        },
        {
            key: 'ExpiryISO',
            headerLabel: 'Expiry',
            header: (
                <Box
                    component="button"
                    type="button"
                    sx={dateHeaderButtonSx}
                    onClick={() => toggleSort('ExpiryISO')}
                >
                    Expiry
                    {sortConfig.key === 'ExpiryISO' &&
                        (sortConfig.direction === 'asc' ? (
                            <ArrowUpwardIcon fontSize="inherit" />
                        ) : (
                            <ArrowDownwardIcon fontSize="inherit" />
                        ))}
                </Box>
            ),
            nowrap: true,
            render: (v) => (v ? fmtDate(v) : '—'),
        },
        {
            key: 'Active',
            header: 'Active',
            headerLabel: 'Active',
            nowrap: true,
            render: (_v, r) =>
                r.ExpiryISO && new Date(r.ExpiryISO) >= new Date() ? <CheckIcon /> : '—',
        },
    ];

    const filtered = useMemo(() => {
        const f = { ...filters };
        Object.keys(f).forEach((k) => (f[k] = String(f[k] || '').trim().toLowerCase()));
        return rows.filter((r) => {
            const name = `${r.first_name || ''} ${r.last_name || ''}`.toLowerCase();
            if (f.q && !name.includes(f.q)) return false;
            if (f.email && !String(r.email || '').toLowerCase().includes(f.email)) return false;
            if (f.city && !String(r.city || '').toLowerCase().includes(f.city)) return false;
            if (f.state && !String(r.state || '').toLowerCase().includes(f.state)) return false;
            return true;
        });
    }, [rows, filters]);

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return filtered;

        const data = [...filtered];
        const { key, direction } = sortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;

        const getValue = (row) => {
            if (key === 'Start') {
                return row.created_at ? new Date(row.created_at) : null;
            }
            if (key === 'ExpiryISO') {
                return row.ExpiryISO ? new Date(row.ExpiryISO) : null;
            }
            return row[key] ?? null;
        };

        data.sort((a, b) => {
            const va = getValue(a);
            const vb = getValue(b);

            if (va === null && vb === null) return 0;
            if (va === null) return 1; // nulls at bottom
            if (vb === null) return -1;

            const ta = va instanceof Date ? va.getTime() : va;
            const tb = vb instanceof Date ? vb.getTime() : vb;

            if (ta < tb) return -1 * multiplier;
            if (ta > tb) return 1 * multiplier;
            return 0;
        });

        return data;
    }, [filtered, sortConfig]);

    /* ---------- PDF Download ---------- */
    const handleDownloadPdf = () => {
        const doc = new jsPDF({
            orientation: 'landscape', // more room for all the columns
            unit: 'pt',
            format: 'a4',
        });

        const titleText = 'Members Report';
        const generatedAt = new Date().toLocaleString();

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(18);
        doc.text(titleText, 40, 40);

        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(80);
        doc.text(`Generated: ${generatedAt}`, 40, 60);
        doc.text(`Total members: ${sortedRows.length}`, 40, 76);

        if (!sortedRows.length) {
            doc.setTextColor(120);
            doc.text('No members match the current filters.', 40, 110);
            doc.save('members-report.pdf');
            return;
        }

        const head = [
            columns.map((col) =>
                col.headerLabel
                    ? col.headerLabel
                    : typeof col.header === 'string'
                        ? col.header
                        : ''
            ),
        ];
        const body = sortedRows.map((row) =>
            columns.map((col) => {
                switch (col.key) {
                    case 'first_name': {
                        const name = `${row.first_name || ''} ${row.last_name || ''}`.trim();
                        return name || '—';
                    }
                    case 'address':
                        return formatAddress(row) || '—';
                    case 'PayFor':
                        return row.PayFor ? 'Yes' : 'No';
                    case 'ExpiryISO':
                        return row.ExpiryISO ? fmtDate(row.ExpiryISO) : '—';
                    case 'Active': {
                        const active =
                            row.ExpiryISO && new Date(row.ExpiryISO) >= new Date();
                        return active ? 'Yes' : 'No';
                    }
                    case 'Start':
                        return row.created_at ? fmtDate(row.created_at) : '—';
                    default: {
                        const raw = row[col.key];
                        return raw === null || raw === undefined || raw === ''
                            ? '—'
                            : String(raw);
                    }
                }
            })
        );

        autoTable(doc, {
            head,
            body,
            startY: 96,
            styles: {
                fontSize: 9,
                cellPadding: 6,
                valign: 'middle',
                overflow: 'linebreak',
            },
            headStyles: {
                fillColor: [33, 150, 243], // soft blue header
                textColor: 255,
                fontSize: 10,
                halign: 'left',
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245], // light gray striping
            },
            bodyStyles: {
                textColor: 40,
            },
            margin: { top: 80, left: 40, right: 40, bottom: 40 },
        });

        doc.save('members-report.pdf');
    };

    const startAdd = () => {
        setEditing(null);
        setForm({
            first_name: '',
            last_name: '',
            email: '',
            phone: '',
            street_address: '',
            city: '',
            state: 'AL',
            zip_code: '',
            membership_type: 'single',
            created_at: new Date().toISOString().slice(0, 10),
            confirmed: 1, // Paid by default
        });
        setOpen(true);
    };
    const startEdit = (r) => {
        setEditing(r);
        setForm({
            first_name: r.first_name || '',
            last_name: r.last_name || '',
            email: r.email || '',
            phone: r.phone || '',
            street_address: r.street_address || '',
            city: r.city || '',
            state: r.state || '',
            zip_code: r.zip_code || '',
            membership_type: r.membership_type || 'single',
            created_at: r.created_at
                ? String(r.created_at).slice(0, 10)
                : new Date().toISOString().slice(0, 10),
            confirmed:
                typeof r.confirmed === 'number'
                    ? r.confirmed
                    : r.PayFor
                        ? 1
                        : 0,
        });
        setOpen(true);
    };

    const save = async () => {
        const payload = {
            first_name: form.first_name?.trim(),
            last_name: form.last_name?.trim(),
            email: form.email?.trim(),
            phone: form.phone?.trim(),
            street_address: form.street_address?.trim(),
            city: form.city?.trim(),
            state: form.state?.trim(),
            zip_code: form.zip_code?.trim(),
            membership_type: form.membership_type,
            created_at: (form.created_at || '').slice(0, 10),
            // "Paid" checkbox controls confirmed (0/1)
            confirmed: form.confirmed ? 1 : 0,
        };

        const resp = editing
            ? await adminUpdateMember(editing.id, payload)
            : await adminCreateMember(payload);

        if (!resp.ok) {
            const data = await resp.json().catch(() => ({}));
            alert(
                Array.isArray(data?.error)
                    ? data.error.join(', ')
                    : data?.error || 'Save failed'
            );
            return;
        }

        setOpen(false);
        await load();
        showBanner(editing ? 'Member updated.' : 'Member created.');
    };

    // open delete dialog instead of window.confirm
    const del = (r) => {
        setDeleteDlg({
            open: true,
            row: r,
            busy: false,
            error: '',
        });
    };

    const confirmDelete = async () => {
        if (!deleteDlg.row) return;
        setDeleteDlg((s) => ({ ...s, busy: true, error: '' }));
        try {
            const resp = await adminDeleteMember(deleteDlg.row.id);
            if (!resp.ok) {
                const data = await resp.json().catch(() => ({}));
                throw new Error(
                    Array.isArray(data?.error)
                        ? data.error.join(', ')
                        : data?.error || 'Delete failed'
                );
            }
            setDeleteDlg({ open: false, row: null, busy: false, error: '' });
            await load();
            showBanner('Member deleted.');
        } catch (e) {
            setDeleteDlg((s) => ({
                ...s,
                busy: false,
                error: e.message || 'Delete failed',
            }));
        }
    };

    const closeDeleteDialog = () => {
        if (deleteDlg.busy) return;
        setDeleteDlg({ open: false, row: null, busy: false, error: '' });
    };

    const fullNameForPreview = `${(form.first_name || '').trim()} ${(form.last_name || '').trim()}`.trim();
    const addressForPreview = formatAddress(form);

    return (
        <Box>
            <Paper
                elevation={1}
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 5,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}
            >
                {banner && (
                    <Alert
                        severity={banner.severity}
                        icon={
                            banner.severity === 'success' ? (
                                <CheckIcon fontSize="small" />
                            ) : undefined
                        }
                        sx={{ mb: 1 }}
                    >
                        {banner.text}
                    </Alert>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        Members
                    </Typography>
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                            alignItems: 'center',
                        }}
                    >
                        {/* Mobile filters button only */}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDlgOpen(true)}
                            sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
                        >
                            Filters
                        </Button>

                        {/* Download PDF button */}
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownloadPdf}
                        >
                            Download PDF
                        </Button>

                        {/* Desktop add button (mobile uses FAB) */}
                        <Button
                            variant="contained"
                            onClick={startAdd}
                            sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                        >
                            Add Member
                        </Button>
                    </Box>
                </Box>

                {/* Desktop Filters */}
                <Grid
                    container
                    spacing={1}
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                    <Grid item xs={12} sm={3}>
                        <TextField
                            size="small"
                            label="Name"
                            fullWidth
                            value={filters.q}
                            onChange={(e) =>
                                setFilters({ ...filters, q: e.target.value })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            size="small"
                            label="Email"
                            fullWidth
                            value={filters.email}
                            onChange={(e) =>
                                setFilters({ ...filters, email: e.target.value })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            size="small"
                            label="City"
                            fullWidth
                            value={filters.city}
                            onChange={(e) =>
                                setFilters({ ...filters, city: e.target.value })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            size="small"
                            label="State"
                            fullWidth
                            value={filters.state}
                            onChange={(e) =>
                                setFilters({ ...filters, state: e.target.value })
                            }
                        />
                    </Grid>
                </Grid>
            </Paper>

            <CrudTable
                ariaLabel="Members list"
                columns={columns}
                rows={sortedRows}
                onEdit={startEdit}
                onDelete={del}
                height="calc(100vh - 320px)"
            />

            {/* Add/Edit dialog with scrollable form area */}
            <Dialog
                open={open}
                onClose={(e, reason) => {
                    if (reason === 'backdropClick' || reason === 'escapeKeyDown')
                        return;
                    setOpen(false);
                }}
                disableEscapeKeyDown
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>{editing ? 'Edit Member' : 'Add Member'}</DialogTitle>
                <DialogContent
                    dividers
                    sx={{
                        p: 0,
                        maxHeight: { xs: '70vh', sm: '75vh' },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Box sx={{ p: 2, overflowY: 'auto' }}>
                        {/* Preview: Name with address directly underneath */}
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {fullNameForPreview || 'New Member'}
                            </Typography>
                            {addressForPreview && (
                                <Typography variant="body2" color="text.secondary">
                                    {addressForPreview}
                                </Typography>
                            )}
                        </Box>

                        {/* Vertical form: each input on its own line */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField
                                label="First Name"
                                fullWidth
                                value={form.first_name || ''}
                                onChange={(e) =>
                                    setForm({ ...form, first_name: e.target.value })
                                }
                            />
                            <TextField
                                label="Last Name"
                                fullWidth
                                value={form.last_name || ''}
                                onChange={(e) =>
                                    setForm({ ...form, last_name: e.target.value })
                                }
                            />
                            <TextField
                                label="Street Address"
                                fullWidth
                                value={form.street_address || ''}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        street_address: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="City"
                                fullWidth
                                value={form.city || ''}
                                onChange={(e) =>
                                    setForm({ ...form, city: e.target.value })
                                }
                            />
                            <TextField
                                label="State"
                                fullWidth
                                value={form.state || ''}
                                onChange={(e) =>
                                    setForm({ ...form, state: e.target.value })
                                }
                            />
                            <TextField
                                label="ZIP"
                                fullWidth
                                value={form.zip_code || ''}
                                onChange={(e) =>
                                    setForm({ ...form, zip_code: e.target.value })
                                }
                            />
                            <TextField
                                label="Email"
                                fullWidth
                                type="email"
                                value={form.email || ''}
                                onChange={(e) =>
                                    setForm({ ...form, email: e.target.value })
                                }
                            />
                            <TextField
                                label="Phone"
                                fullWidth
                                value={form.phone || ''}
                                onChange={(e) =>
                                    setForm({ ...form, phone: e.target.value })
                                }
                            />
                            <TextField
                                select
                                fullWidth
                                label="Membership Type"
                                value={form.membership_type || 'single'}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        membership_type: e.target.value,
                                    })
                                }
                            >
                                {TYPE_OPTIONS.map((o) => (
                                    <MenuItem key={o.value} value={o.value}>
                                        {o.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                fullWidth
                                required
                                label="Date Added"
                                type="date"
                                InputLabelProps={{ shrink: true }}
                                value={(form.created_at || '').slice(0, 10)}
                                onChange={(e) =>
                                    setForm({ ...form, created_at: e.target.value })
                                }
                                helperText="Used as the membership start date"
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={!!form.confirmed}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                confirmed: e.target.checked ? 1 : 0,
                                            })
                                        }
                                    />
                                }
                                label="Paid"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete confirmation dialog */}
            <Dialog
                open={deleteDlg.open}
                onClose={closeDeleteDialog}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Delete Member</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                        Are you sure you want to delete{' '}
                        <strong>
                            {deleteDlg.row
                                ? `${deleteDlg.row.first_name || ''} ${
                                deleteDlg.row.last_name || ''
                            }`.trim() || 'this member'
                                : 'this member'}
                        </strong>
                        ?
                    </Typography>
                    {deleteDlg.error && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                            {deleteDlg.error}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} disabled={deleteDlg.busy}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={confirmDelete}
                        disabled={deleteDlg.busy}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Mobile filter dialog */}
            <FilterDialog
                open={filterDlgOpen}
                initial={filters}
                onSearch={(vals) => {
                    setFilters(vals);
                    setFilterDlgOpen(false);
                }}
                onClear={() => {
                    setFilters({ q: '', email: '', city: '', state: '' });
                    setFilterDlgOpen(false);
                }}
                onCancel={() => setFilterDlgOpen(false)}
            />

            <EmailDialog
                open={emailDlg.open}
                name={emailDlg.name}
                email={emailDlg.email}
                onClose={() => setEmailDlg({ open: false, name: '', email: '' })}
                onSent={() => setSnack(true)}
            />

            <Snackbar
                open={snack}
                autoHideDuration={3000}
                onClose={() => setSnack(false)}
            >
                <Alert severity="success" onClose={() => setSnack(false)}>
                    Message sent.
                </Alert>
            </Snackbar>

            {/* Mobile FAB to add member */}
            <Fab
                color="primary"
                onClick={startAdd}
                sx={{
                    position: 'fixed',
                    right: 16,
                    bottom: 16,
                    display: { xs: 'flex', sm: 'none' },
                }}
                aria-label="Add member"
            >
                <AddIcon />
            </Fab>
        </Box>
    );
}
